import * as React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";

import { useAuth } from "lib/auth";
import { getHospitalGroup } from "lib/extrahorizon/group";
import { exhFbcSettingsConverter, exhWbcSettingsConverter } from "lib/extrahorizon/settings/converters";
import { getNormalRanges } from "lib/helpers";
import { ExhFbcSettingsData, ExhWbcSettingsData } from "types/measurement";

import { useExhSettingsDocument } from "../hooks/useExhSettingsDocument";

import { HospitalConfiguration, HospitalConfigurationType, HospitalRegion } from "./types";

const defaultConfig: HospitalConfiguration = {
  wbcRange: {
    min: 0,
    max: 10,
  },
  neutrophilRange: {
    min: 0,
    max: 10,
  },
  haemoglobinRangeMale: {
    min: 0,
    max: 280,
  },
  haemoglobinRangeFemale: {
    min: 0,
    max: 280,
  },
  plateletRange: {
    min: 0,
    max: 800,
  },
  confirmatoryThreshold: 1.2,
};

const DEFAULT_REGION: HospitalRegion = "USA";

export const HospitalSettingConfigContext = React.createContext<HospitalConfigurationType>({
  hospitalConfig: defaultConfig,
  normalRanges: getNormalRanges(defaultConfig) as NormalRanges,
  region: DEFAULT_REGION,
  refetch: async () => {},
});

export const HospitalConfigurationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { showBoundary: onError } = useErrorBoundary();
  const { currentUser } = useAuth()[1];
  const hospitalId = currentUser?.hospital?.id ?? null;
  const userRegion = currentUser?.hospital?.region;

  const [region, setRegion] = useState<HospitalRegion | null>(userRegion ?? null);

  useEffect(() => {
    if (userRegion) {
      setRegion(userRegion);
      return;
    }
    if (!hospitalId) {
      return;
    }
    let cancelled = false;
    getHospitalGroup(hospitalId)
      .then((group) => {
        if (!cancelled) {
          setRegion(group.region);
        }
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        const status = (err as { status?: number } | null)?.status;
        if (status === 401 || status === 403) {
          setRegion(DEFAULT_REGION);
          return;
        }
        onError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [hospitalId, userRegion, onError]);

  const wbcResult = useExhSettingsDocument<ExhWbcSettingsData>(region === "USA" ? "measurement-wbc-settings" : null);
  const fbcResult = useExhSettingsDocument<ExhFbcSettingsData>(region === "UK" ? "measurement-fbc-settings" : null);

  useEffect(() => {
    const error = wbcResult.error || fbcResult.error;
    if (error) {
      onError(error);
    }
  }, [wbcResult.error, fbcResult.error, onError]);

  const context = useMemo<HospitalConfigurationType>(() => {
    const effectiveRegion = region ?? DEFAULT_REGION;
    const activeResult = effectiveRegion === "UK" ? fbcResult : wbcResult;
    const partial =
      effectiveRegion === "UK"
        ? exhFbcSettingsConverter(fbcResult.document)
        : exhWbcSettingsConverter(wbcResult.document);
    const hospitalConfig: HospitalConfiguration = { ...defaultConfig, ...partial };
    return {
      hospitalConfig,
      normalRanges: getNormalRanges(hospitalConfig) as NormalRanges,
      region: effectiveRegion,
      documentId: activeResult.document?.id,
      refetch: activeResult.refetch,
    };
  }, [region, wbcResult, fbcResult]);

  return <HospitalSettingConfigContext.Provider value={context}>{children}</HospitalSettingConfigContext.Provider>;
};

export const useHospitalConfiguration = (): HospitalConfigurationType => useContext(HospitalSettingConfigContext);
